import { Container } from 'react-bootstrap'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../common/integrations'
import './IntegrationAbout.scss'
import React from 'react'
import { Link } from 'react-router-dom'
import { HowItWorksComponent } from './HowItWorks/HowItWorksComponent'

const PhoneAbout: React.FC = () => {
  return (
    <>
      <p>
        The Phone integration allows you to claim and manage phone numbers directly from your Professional Dashboard account. With this integration, you can set
        up business phone lines, manage call forwarding, and access voicemail features to enhance your communication with clients.
      </p>
      <p>Key features include:</p>
      <ul>
        <li>Claim new phone numbers for your business</li>
        <li>Manage multiple phone lines</li>
        <li>Set up call forwarding and voicemail</li>
        <li>Access call logs and analytics</li>
      </ul>
      <p>To get started, connect the Phone integration and follow the prompts to claim your desired phone number.</p>
    </>
  )
}

export interface IntegrationAboutTabProps {
  serviceId: IntegrationServiceId
}

const AboutComponent: React.FC<{ serviceId: IntegrationServiceId }> = ({ serviceId }) => {
  switch (serviceId) {
    case IntegrationServiceId.PHONE:
      return <PhoneAbout />
    case IntegrationServiceId.ZOOM:
      return (
        <div>
          Connect your Zoom and Zocks accounts to capture notes and tasks. With Zocks native app for Zoom, you can also get access to everything you need,
          manage tasks and sync client data right within Zoom.
        </div>
      )
    case IntegrationServiceId.MEET:
    case IntegrationServiceId.TEAMS:
      return (
        <div>
          Easily sync with your calendar or{' '}
          <Link to='/workspace/upcoming' className='common-card-description-link' onClick={(e) => e.stopPropagation()} target='_blank'>
            invite
          </Link>{' '}
          your Zocks AI Notetaker to capture notes and tasks, so you never miss a detail.
        </div>
      )
    case IntegrationServiceId.WEBEX:
      return <div>Connect Zocks with Webex to capture notes and tasks, so you never miss a detail.</div>
    case IntegrationServiceId.GOOGLE_CALENDAR:
      return (
        <div>Sync your meetings effortlessly with your Google Calendar to stay on top of your schedule and never miss an important client conversation.</div>
      )
    case IntegrationServiceId.OUTLOOK_CALENDAR:
      return (
        <div>Sync your meetings effortlessly with your Outlook Calendar to stay on top of your schedule and never miss an important client conversation.</div>
      )
    case IntegrationServiceId.EXCHANGE_CALENDAR:
      return (
        <div>Sync your meetings effortlessly with your Exchange Calendar to stay on top of your schedule and never miss an important client conversation.</div>
      )
    case IntegrationServiceId.GMAIL:
      return (
        <div>
          Connect your accounts to send emails through your Gmail account. Turn your email into a data source for client insights, automated task creation, and
          profile updates.
        </div>
      )
    case IntegrationServiceId.OUTLOOK_MAIL:
      return (
        <div>
          Send emails through your Outlook account. Turn your email into a data source for client insights, automated task creation, and profile updates.
          Increase your productivity with Zocks' Native Outlook App.
        </div>
      )
    case IntegrationServiceId.E_MONEY:
      return (
        <div>
          Keep your planning data effortlessly up-to-date. Automatically sync contacts, prep for meetings, and easily update 200+ eMoney Facts with insights
          from conversations, emails, documents, and other connected systems, all without manual data entry.
        </div>
      )
    case IntegrationServiceId.RIGHT_CAPITAL:
      return (
        <div>
          After every client conversation, Zocks automatically captures and easily syncs client information to RightCapital, so you can create or update client
          profiles faster and close more prospects.
        </div>
      )
    case IntegrationServiceId.CONQUEST_PLANNING:
      return <div>Get real-time planning data and insights for meeting prep, live meetings, emails and forms.</div>
    case IntegrationServiceId.ORION:
    case IntegrationServiceId.BLACK_DIAMOND_WP:
      return <div>Get real-time portfolio data and insights for meeting prep, live meetings, emails and forms.</div>
    case IntegrationServiceId.HOLISTIPLAN:
      return <div>Unlock new planning opportunities with your clients by connecting Holistiplan’s advanced tax analysis with Zocks.</div>
    case IntegrationServiceId.WEALTHBOX:
      return (
        <div>
          Keep your Wealthbox CRM data effortlessly up-to-date. Automatically sync, create and update contact records, save notes, create tasks, trigger
          workflows, and prep for meetings with insights captured by Zocks from conversations, emails, documents, and other connected systems - all without
          manual data entry.
        </div>
      )
    case IntegrationServiceId.SALESFORCE:
    case IntegrationServiceId.PRACTIFI:
    case IntegrationServiceId.XLR8:
    case IntegrationServiceId.ELEMENTS:
    case IntegrationServiceId.BLACK_DIAMOND:
    case IntegrationServiceId.REDTAIL:
    case IntegrationServiceId.ZOHO:
    case IntegrationServiceId.HUBSPOT:
    case IntegrationServiceId.DYNAMICS:
    case IntegrationServiceId.ENGAGE:
    case IntegrationServiceId.SMARTOFFICE:
      return (
        <div>
          Keep your {getIntegrationDisplayName(serviceId)} CRM data effortlessly up-to-date. Automatically sync, create and update contact, save notes, create
          tasks, and prep for meetings with insights captured by Zocks from conversations, emails, documents, and other connected systems - all without manual
          data entry.
        </div>
      )
    case IntegrationServiceId.PRECISE_FP:
      return (
        <div>
          Keep your PreciseFP data up to date and synced across all your connected systems with insights captured by Zocks from conversations, emails, documents
          - all without manual data entry.
        </div>
      )
    case IntegrationServiceId.WEALTH:
      return (
        <div>
          Connect Wealth.com and Zocks to see estate planning details in your workflow — so you walk into meetings prepared and keep plans moving to completion.
        </div>
      )
    case IntegrationServiceId.WOVE:
      return <div>Get real-time portfolio data and insights for meeting prep, live meetings, emails and forms.</div>
    case IntegrationServiceId.RINGCENTRAL:
      return <div>Connect your RingCentral phone system to Zocks. No need to conference Zocks in manually, your AI Assistant joins calls automatically.</div>
  }
  return <div>No additional information available for this integration.</div>
}

export const IntegrationAboutTab: React.FC<IntegrationAboutTabProps> = ({ serviceId }) => {
  return (
    <Container fluid>
      <h4 className='mb-3'>Overview</h4>
      <div className='about-container p-3 w-75'>
        <AboutComponent serviceId={serviceId} />
      </div>
      <div className='how-it-works-container mt-5 mb-3 w-75 d-flex flex-column p-3 justify-content-center align-items-start'>
        <h4 className='how-it-works-title mb-3'>How it works</h4>
        <div>
          <HowItWorksComponent serviceId={serviceId} />
        </div>
      </div>
    </Container>
  )
}
